import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { cn } from '../utils/cn';
import { glassRadius } from './GlassSurface';
import { springSoft } from './motion';

export interface GlassTopBarProps {
  /** 11px tracked kicker above the title. */
  eyebrow?: ReactNode;
  title?: ReactNode;
  /** Search, a period selector, a scenario switcher. */
  center?: ReactNode;
  actions?: ReactNode;
  /** Distance from the viewport edges, matching the rail. */
  inset?: number;
  /** Left offset so the bar clears the rail — pass GLASS_NAV_WIDTH + inset. */
  offsetLeft?: number;
  floating?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * The floating utility bar. Same material and inset as the rail, so the two
 * read as one piece of chrome hovering over the canvas.
 */
export function GlassTopBar({
  eyebrow,
  title,
  center,
  actions,
  inset = 12,
  offsetLeft = 0,
  floating = true,
  className,
  children,
}: GlassTopBarProps) {
  return (
    <motion.header
      className={cn('g-surface z-30 flex items-center gap-4 px-5 py-3', className)}
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springSoft}
      style={{
        borderRadius: glassRadius.xl,
        ...(floating
          ? { position: 'fixed', top: inset, right: inset, left: offsetLeft || inset }
          : { position: 'relative' }),
      }}
    >
      {title || eyebrow ? (
        <div className="flex min-w-0 flex-col gap-0.5">
          {eyebrow ? <span className="g-label truncate">{eyebrow}</span> : null}
          {title ? (
            <span
              className="truncate text-[15px] font-medium tracking-[-0.01em]"
              style={{ color: 'var(--g-text)' }}
            >
              {title}
            </span>
          ) : null}
        </div>
      ) : null}

      {center ? (
        <div className="flex min-w-0 flex-1 justify-center">{center}</div>
      ) : (
        <div className="flex-1" />
      )}

      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      {children}
    </motion.header>
  );
}
